/**
 * MCP Tools for Engagements Management
 */

import { MCPTool } from '../types';
import { engagementsService } from '../../services/engagementsService';
import { utilizationService } from '../../services/utilizationService';
import { engagementAIService } from '../../services/engagementAIService';
import { 
  EngagementType, 
  ENGAGEMENT_TYPE_CONFIG,
  QuickCreateFormData 
} from '../../types/engagement';

export const createEngagementTool: MCPTool = {
  name: 'create_engagement',
  description: 'Creates a new engagement/project. Use when user says: "Neuer Auftrag", "Projekt anlegen", "Engagement erstellen", "Managed Service einrichten". Supports types: project, managed_service, poc, pilot, workshop, audit, maintenance, body_leasing.',
  
  inputSchema: {
    type: 'object',
    properties: {
      name: {
        type: 'string',
        description: 'Engagement name (required)'
      },
      engagementType: {
        type: 'string',
        enum: ['project', 'managed_service', 'body_leasing', 'prestudy', 'workshop', 'hackathon', 'poc', 'pilot', 'audit', 'maintenance'],
        description: 'Type of engagement (default: project)'
      },
      description: {
        type: 'string',
        description: 'Detailed description'
      },
      customerName: {
        type: 'string',
        description: 'Customer name (leave empty for internal projects)'
      },
      startDate: {
        type: 'string',
        description: 'Start date in ISO 8601 format (e.g., "2026-03-01")'
      }
    },
    required: ['name']
  },

  async execute(params, context) {
    // If user provides description but no type, try to detect type
    let engagementType: EngagementType = params.engagementType || 'project';
    
    if (!params.engagementType && params.description) {
      try {
        const detection = await engagementAIService.detectEngagementType(params.description);
        if (detection.confidence > 0.7) {
          engagementType = detection.suggestedType;
        }
      } catch (e) {
        // Use default
      }
    }

    const data: QuickCreateFormData = {
      name: params.name,
      engagementType,
      customerNameManual: params.customerName,
      startDate: params.startDate ? new Date(params.startDate) : new Date(),
      managerId: context.userId
    };

    const engagementId = await engagementsService.quickCreateEngagement(context.orgId, data);
    const engagement = await engagementsService.getEngagement(engagementId);

    return {
      success: true,
      engagement,
      message: `Auftrag "${params.name}" als ${ENGAGEMENT_TYPE_CONFIG[engagementType].label.de} erstellt.`
    };
  }
};

export const listEngagementsTool: MCPTool = {
  name: 'list_engagements',
  description: 'Lists all engagements/projects. Use when user asks: "Zeige Aufträge", "Meine Projekte", "Aktive Engagements", "Welche Aufträge habe ich?"',
  
  inputSchema: {
    type: 'object',
    properties: {
      status: {
        type: 'string',
        enum: ['draft', 'planning', 'active', 'on_hold', 'completed', 'cancelled'],
        description: 'Filter by status'
      },
      type: {
        type: 'string',
        enum: ['project', 'managed_service', 'body_leasing', 'poc', 'pilot', 'workshop', 'audit', 'maintenance'],
        description: 'Filter by type'
      },
      myOnly: {
        type: 'boolean',
        description: 'Only show engagements where user is manager or team member'
      }
    }
  },

  async execute(params, context) {
    const filters: any = {};
    
    if (params.status) {
      filters.statuses = [params.status];
    } else {
      filters.statuses = ['active', 'planning'];
    }
    
    if (params.type) {
      filters.engagementTypes = [params.type];
    }

    let engagements = await engagementsService.getEngagements(context.orgId, filters);

    if (params.myOnly) {
      engagements = engagements.filter(e => 
        e.managerId === context.userId ||
        e.teamAllocations.some(ta => ta.userId === context.userId)
      );
    }

    const summary = engagements.map(e => ({
      id: e.id,
      name: e.name,
      type: ENGAGEMENT_TYPE_CONFIG[e.engagementType].label.de,
      status: e.status,
      customer: e.customer?.name || e.customerNameManual || 'Intern',
      progress: e.progressPercentage || 0
    }));

    return {
      success: true,
      count: engagements.length,
      engagements: summary,
      message: `${engagements.length} Aufträge gefunden.`
    };
  }
};

export const getTeamUtilizationTool: MCPTool = {
  name: 'get_team_utilization',
  description: 'Shows team utilization/workload. Use when user asks: "Team-Auslastung", "Wer ist verfügbar?", "Wer ist überbucht?", "Kapazität"',
  
  inputSchema: {
    type: 'object',
    properties: {}
  },

  async execute(params, context) {
    const utilization = await utilizationService.calculateTeamUtilization(context.orgId);
    const overallocated = utilization.filter(u => u.isOverallocated);
    const available = utilization.filter(u => u.availableCapacity >= 50);
    const avgUtilization = utilizationService.calculateAverageUtilization(utilization);

    const summary = utilization.map(u => ({
      name: u.userName,
      utilization: u.totalAllocation,
      available: u.availableCapacity,
      status: u.isOverallocated ? 'überbucht' : 
              u.availableCapacity >= 50 ? 'verfügbar' : 'ausgelastet',
      engagements: u.engagements.map(e => e.engagementName)
    }));

    return {
      success: true,
      averageUtilization: avgUtilization,
      overallocatedCount: overallocated.length,
      availableCount: available.length,
      teamMembers: summary,
      message: `Team-Auslastung: ${avgUtilization}% Durchschnitt. ${overallocated.length} überbucht, ${available.length} verfügbar.`
    };
  }
};

export const getExpiringContractsTool: MCPTool = {
  name: 'get_expiring_contracts',
  description: 'Lists contracts expiring soon. Use when user asks: "Auslaufende Verträge", "Welche Verträge müssen verlängert werden?", "Contract renewal"',
  
  inputSchema: {
    type: 'object',
    properties: {
      withinDays: {
        type: 'number',
        description: 'Number of days to look ahead (default: 90)'
      }
    }
  },

  async execute(params, context) {
    const withinDays = params.withinDays || 90;
    const expiring = await engagementsService.getExpiringContracts(context.orgId, withinDays);

    const summary = expiring.map(e => ({
      id: e.id,
      name: e.name,
      type: ENGAGEMENT_TYPE_CONFIG[e.engagementType].label.de,
      customer: e.customer?.name || e.customerNameManual || 'Intern',
      expiresOn: e.contractEndDate || e.endDate,
      renewalStatus: e.contractRenewal?.renewalStatus || 'pending'
    }));

    return {
      success: true,
      count: expiring.length,
      contracts: summary,
      message: expiring.length > 0 
        ? `${expiring.length} Vertrag${expiring.length > 1 ? 'e laufen' : ' läuft'} in den nächsten ${withinDays} Tagen aus.`
        : 'Keine auslaufenden Verträge in diesem Zeitraum.'
    };
  }
};

export const getEngagementTool: MCPTool = {
  name: 'get_engagement',
  description: 'Gets details of a specific engagement. Use when user asks about a specific project by name or ID.',
  
  inputSchema: {
    type: 'object',
    properties: {
      engagementId: {
        type: 'string',
        description: 'Engagement ID'
      },
      name: {
        type: 'string',
        description: 'Search by name (partial match)'
      }
    }
  },

  async execute(params, context) {
    if (params.engagementId) {
      const engagement = await engagementsService.getEngagement(params.engagementId);
      if (!engagement) {
        return { success: false, message: 'Auftrag nicht gefunden.' };
      }
      return { success: true, engagement };
    }

    if (params.name) {
      const engagements = await engagementsService.getEngagements(context.orgId, {
        searchQuery: params.name
      });
      
      if (engagements.length === 0) {
        return { success: false, message: `Kein Auftrag mit "${params.name}" gefunden.` };
      }
      
      if (engagements.length === 1) {
        return { success: true, engagement: engagements[0] };
      }
      
      return {
        success: true,
        message: `${engagements.length} Aufträge gefunden:`,
        engagements: engagements.map(e => ({ id: e.id, name: e.name }))
      };
    }

    return { success: false, message: 'Bitte ID oder Name angeben.' };
  }
};

export const generateRecurringTasksTool: MCPTool = {
  name: 'generate_recurring_tasks',
  description: 'Generates recurring tasks for managed services and maintenance engagements. Use when user says: "Generiere Tasks", "Wiederkehrende Aufgaben erstellen"',
  
  inputSchema: {
    type: 'object',
    properties: {
      engagementId: {
        type: 'string',
        description: 'Specific engagement ID (optional, generates for all if not provided)'
      }
    }
  },

  async execute(params, context) {
    if (params.engagementId) {
      const taskIds = await engagementsService.generateRecurringTasks(params.engagementId);
      return {
        success: true,
        tasksCreated: taskIds.length,
        message: `${taskIds.length} wiederkehrende Task(s) erstellt.`
      };
    }

    const results = await engagementsService.generateAllRecurringTasks(context.orgId);
    const totalTasks = results.reduce((sum, r) => sum + r.tasksCreated, 0);
    
    return {
      success: true,
      engagementsProcessed: results.length,
      totalTasksCreated: totalTasks,
      message: `${totalTasks} wiederkehrende Task(s) für ${results.length} Auftrag/Aufträge erstellt.`
    };
  }
};

// Export all tools
export const engagementTools: MCPTool[] = [
  createEngagementTool,
  listEngagementsTool,
  getTeamUtilizationTool,
  getExpiringContractsTool,
  getEngagementTool,
  generateRecurringTasksTool
];
